/**
 * Single source of truth for admin catalog: dropdown (HomeIntroMenu) and tool grid (DashBoard).
 * Keep paths in sync with AppAdmin.tsx routes.
 */
export const ADMIN_NAV_SECTION_IDS = ["operations", "directory", "finance", "content", "compliance"] as const;

export type AdminNavSectionId = typeof ADMIN_NAV_SECTION_IDS[number];

export type AdminNavItem = {
	/** route path (must match TransformRoute in AppAdmin) */
	to: string;
	icon: string;
	/** Short label for menu + cards */
	label: string;
	/** One-line help on dashboard */
	description: string;
	section: AdminNavSectionId;
	/** Order within the section (lower first) */
	order: number;
	/** RBAC permission key required to view this item. Omit/null = always visible. */
	permission?: string | null;
	/**
	 * Gate-only entry: PermissionRoute still enforces `permission`, but the item is
	 * hidden from the dashboard grid, menus, and My access. Use for detail pages
	 * reached via in-app navigation (not standalone tools).
	 */
	hidden?: boolean;
	/** Chỉ hiện cho super admin (users.is_super_admin) — thêm vào điều kiện `permission`, không thay thế. */
	superAdminOnly?: boolean;
};

export const ADMIN_NAV_SECTION_LABELS: Record<AdminNavSectionId, string> = {
	operations: "Operations",
	directory: "Directory & network",
	finance: "Finance & payments",
	content: "Content & comms",
	compliance: "System & compliance",
};

export const ADMIN_NAV_CATALOG: AdminNavItem[] = [
	// Operations
	{
		to: "/booking",
		icon: "fa fa-calendar",
		label: "Bookings",
		description: "Orders, status, exports.",
		section: "operations",
		order: 10,
		permission: "orders.view",
	},
	{
		to: "/station-requests",
		icon: "fa fa-inbox",
		label: "Station requests",
		description: "Inbox and approvals.",
		section: "operations",
		order: 20,
		permission: "stations.manage",
	},
	{
		to: "/service-interest-clicks",
		icon: "fa fa-line-chart",
		label: "Service demand",
		description: "Add-on interest, demand.",
		section: "operations",
		order: 30,
		permission: "analytics.service-demand.view",
	},
	{
		to: "/search-tracking",
		icon: "fa fa-search",
		label: "Search tracking",
		description: "Queries, no results, Near me.",
		section: "operations",
		order: 40,
		permission: "analytics.search-tracking.view",
	},
	{
		to: "/station-portal-logs",
		icon: "fa fa-exclamation-triangle",
		label: "Station portal logs",
		description: "API errors, station events.",
		section: "operations",
		order: 50,
		permission: "analytics.station-portal-logs.view",
	},
	// Directory
	{
		to: "/user",
		icon: "fa fa-users",
		label: "Users",
		description: "Customer accounts.",
		section: "directory",
		order: 10,
		permission: "customers.view",
	},
	{
		to: "/partner",
		icon: "fa fa-handshake-o",
		label: "Stations",
		description: "Profiles, pricing, settings.",
		section: "directory",
		order: 20,
		permission: "partners.view",
	},
	{
		// Quân 26/10/2026: hàng chờ duyệt giấy phép kinh doanh (trước chỉ thấy khi mở từng trạm).
		to: "/business-licences",
		icon: "fa fa-id-card-o",
		label: "Business licences",
		description: "Review licences so stations can request payouts.",
		section: "directory",
		order: 25,
		permission: "partners.view",
	},
	{
		// Quân chốt 17/10/2026: bên ký (pháp nhân) làm gốc; chuỗi = bên quản lý có uỷ quyền.
		to: "/legal-entities",
		icon: "fa fa-building",
		label: "Legal entities & chains",
		description: "Who signs and gets paid; chain names.",
		section: "directory",
		order: 30,
		permission: "partners.view",
	},
	{
		// Bảng giá tính từ dữ liệu quét đối thủ; chỉ đổi giá thật khi bấm Apply. Super admin + partners.manage.
		to: "/price-plans",
		icon: "fa fa-usd",
		label: "Price plans",
		description: "Review and apply station price plans.",
		section: "directory",
		order: 40,
		permission: "partners.manage",
		superAdminOnly: true,
	},
	// Finance
	{
		to: "/debt-management",
		icon: "fa fa-pie-chart",
		label: "Debt & settlement",
		description: "Receivables, settlement, roll-ups.",
		section: "finance",
		order: 10,
		permission: "finance.debt.view",
	},
	{
		to: "/payout-batches",
		icon: "fa fa-object-group",
		label: "Batch payouts",
		description: "One Wise transfer per legal entity.",
		section: "finance",
		order: 15,
		permission: "settlements.view",
	},
	{
		to: "/debt-history",
		icon: "fa fa-history",
		label: "Debt history",
		description: "Past balances, reconciliation.",
		section: "finance",
		order: 20,
		permission: "finance.debt.history.view",
	},
	// Gate-only detail pages (reached from Debt & settlement — not standalone tools).
	// Without these entries PermissionRoute treats the routes as ungated.
	{
		to: "/debt-detail",
		icon: "fa fa-pie-chart",
		label: "Debt detail",
		description: "Per-station debt detail.",
		section: "finance",
		order: 21,
		permission: "finance.debt.view",
		hidden: true,
	},
	{
		to: "/partner-debt-detail",
		icon: "fa fa-pie-chart",
		label: "Station debt detail",
		description: "Per-station debt detail.",
		section: "finance",
		order: 22,
		permission: "finance.debt.view",
		hidden: true,
	},
	{
		to: "/payment-channel-number",
		icon: "fa fa-credit-card",
		label: "Payment channels",
		description: "Channels, bank details.",
		section: "finance",
		order: 30,
		permission: "finance.channels.view",
	},
	{
		to: "/payment-cycle-settings",
		icon: "fa fa-calendar-check-o",
		label: "Payment cycles",
		description: "Cutoffs, payout cycles.",
		section: "finance",
		order: 40,
		permission: "finance.cycles.view",
	},
	{
		to: "/payment-surcharge-settings",
		icon: "fa fa-paypal",
		label: "PayPal / non-Stripe surcharges",
		description: "Optional fees by channel.",
		section: "finance",
		order: 50,
		permission: "finance.surcharges.manage",
	},
	{
		to: "/country-rates",
		icon: "fa fa-globe",
		label: "Country rates",
		description: "Pricing by market.",
		section: "finance",
		order: 60,
		permission: "finance.rates.view",
	},
	// Content
	{
		to: "/content-manage",
		icon: "fa fa-file-text-o",
		label: "Content",
		description: "Landing, CMS, blocks.",
		section: "content",
		order: 10,
		permission: "content.manage",
	},
	{
		to: "/text-editor?scope=admin",
		icon: "fa fa-font",
		label: "Text Editor",
		description: "Unified text workspace: admin, landing, stations.",
		section: "content",
		order: 20,
		permission: "content.manage",
	},
	{
		to: "/top-destination-manage",
		icon: "fa fa-map-marker",
		label: "Top destinations",
		description: "Homepage destination slots.",
		section: "content",
		order: 50,
		permission: "content.manage",
	},
	{
		to: "/format-email",
		icon: "fa fa-envelope-o",
		label: "Email templates",
		description: "Transactional email.",
		section: "content",
		order: 60,
		permission: "comms.manage",
	},
	// Operations
	{
		to: "/support-categories",
		icon: "fa fa-tags",
		label: "Support config",
		description: "Ticket categories & priority levels.",
		section: "operations",
		order: 60,
		permission: "support.manage",
	},
	// System & compliance
	{
		to: "/",
		icon: "fa fa-th-large",
		label: "All tools",
		description: "Full list of admin screens.",
		section: "compliance",
		order: 5,
		permission: null,
	},
	{
		to: "/system-config",
		icon: "fa fa-cog",
		label: "System config",
		description: "Flags, messages, parameters.",
		section: "compliance",
		order: 20,
		permission: "system.config.manage",
	},
	{
		to: "/notifications",
		icon: "fa fa-bell",
		label: "System notifications",
		description: "Banners, timing, audience.",
		section: "compliance",
		order: 30,
		permission: "system.notifications.manage",
	},
	{
		to: "/audit-logs",
		icon: "fa fa-history",
		label: "Audit log",
		description: "Important admin changes.",
		section: "compliance",
		order: 40,
		permission: "compliance.audit.view",
	},
	{
		to: "/consent-logs",
		icon: "fa fa-shield",
		label: "Consent log",
		description: "User consent events.",
		section: "compliance",
		order: 50,
		permission: "compliance.consent.view",
	},
	{
		to: "/audit-timeline",
		icon: "fa fa-clock-o",
		label: "Activity Timeline",
		description: "Full audit event stream (super admin only).",
		section: "compliance",
		order: 45,
		permission: "compliance.audit.timeline.view",
	},
];

function sortBySectionThenOrder(a: AdminNavItem, b: AdminNavItem): number {
	const sa = ADMIN_NAV_SECTION_IDS.indexOf(a.section);
	const sb = ADMIN_NAV_SECTION_IDS.indexOf(b.section);
	if (sa !== sb) return sa - sb;
	if (a.order !== b.order) return a.order - b.order;
	return a.label.localeCompare(b.label, "en");
}

/**
 * Small curated set for the header dropdown only. Full catalog stays on the `/` tool grid.
 * Keep this short; order is display order in the menu.
 */
export const ADMIN_DROPDOWN_PATHS: readonly string[] = [
	"/booking",
	"/station-requests",
	"/user",
	"/partner",
	"/debt-management",
	"/system-config",
] as const;

export function getAdminNavItemByTo(to: string): AdminNavItem | undefined {
	const exact = ADMIN_NAV_CATALOG.find((i) => i.to === to);
	if (exact) return exact;
	// Router paths carry no query-string while some catalog entries do
	// (e.g. "/text-editor?scope=admin") — fall back to pathname comparison,
	// otherwise those routes silently skip their permission gate.
	const path = to.split("?")[0];
	return ADMIN_NAV_CATALOG.find((i) => i.to.split("?")[0] === path);
}

/** Short list for the avatar dropdown (flat, no section headers). */
export function getAdminNavItemsForMenu(): AdminNavItem[] {
	return ADMIN_DROPDOWN_PATHS.map((to) => getAdminNavItemByTo(to)).filter(
		(item): item is AdminNavItem => item != null
	);
}

/** All tools for the dashboard grid, including self-link; grouped by section. */
export function getAdminNavItemsForDashboard(): AdminNavItem[] {
	return [...ADMIN_NAV_CATALOG].filter((item) => item.to !== "/" && !item.hidden).sort(sortBySectionThenOrder);
}

export function groupAdminNavBySection(items: AdminNavItem[]): { section: AdminNavSectionId; items: AdminNavItem[] }[] {
	const map = new Map<AdminNavSectionId, AdminNavItem[]>();
	for (const id of ADMIN_NAV_SECTION_IDS) {
		map.set(id, []);
	}
	for (const item of items) {
		map.get(item.section)?.push(item);
	}
	return ADMIN_NAV_SECTION_IDS.map((section) => ({
		section,
		items: map.get(section) ?? [],
	})).filter((g) => g.items.length > 0);
}
